import { getShareOrigin } from '../services/api';
import { SHARE_REF_PARAM, SHARE_REF_VALUE } from '../services/shareAnalytics';

/**
 * Every shareable surface in the app (cards, detail pages, profiles) goes
 * through this helper so links land on the canonical brand domain when
 * generated from preview deployments and stay consistent across native + web.
 *
 * Mapping mirrors the route table in `src/navigation` and the public web
 * paths exposed by Vercel rewrites. Keep these in sync if any route is
 * renamed.
 */
export type ShareableType =
  | 'live'         // /live/:id — live stream / event detail
  | 'room'         // /room/:id — practice room (live + archive)
  | 'practice'     // /practices/:id — pre-recorded practice
  | 'event'        // /lives?eventId=:id — offline event (opens in the calendar modal)
  | 'master'       // /u/:userId — master public profile
  | 'user'         // /u/:userId — user public profile
  | 'master_post'  // /u/:userId/post/:postId — master post
  // CORE-966 — a «Путь Света» card, addressed by its opaque share token
  // rather than by its owner. This is the only share link whose id is not a
  // user- or content-id: the token is what makes the card readable by a
  // link-preview crawler without opening the rest of the profile.
  | 'gamification'; // /s/:token

export interface BuildShareUrlOptions {
  /** Optional secondary id (post id, comment id, etc.). */
  subId?: string | number;
  /** Optional query string params. URL-encoded for you. */
  query?: Record<string, string | number | undefined>;
}

export function buildShareUrl(
  type: ShareableType,
  id: string | number,
  options: BuildShareUrlOptions = {},
): string {
  const origin = getShareOrigin();
  const path = pathFor(type, id, options.subId);
  // CORE-788 — every URL this helper produces is, by definition, a link somebody
  // is about to share. Stamping the marker here rather than at ~20 call sites is
  // what makes the inbound half of the funnel complete instead of sampled: the
  // click counters alone say only that people pressed the button, never whether
  // sharing brings anyone in.
  const qs = serializeQuery({
    ...options.query,
    [SHARE_REF_PARAM]: SHARE_REF_VALUE,
  });
  // `event` already carries a query string (`/lives?eventId=`), so extra params
  // have to join with `&` rather than start a second `?`.
  return `${origin}${path}${path.includes('?') ? '&' : '?'}${qs}`;
}

/**
 * CORE-2063 — the bare canonical address, without the CORE-788 share marker or
 * any other query: what a printed or on-screen QR code carries. A QR is read by
 * a stranger's camera, so it holds only the public path — no tokens, no ref —
 * and the shorter text keeps the symbol small enough to scan from a stage.
 */
export function buildCanonicalUrl(type: ShareableType, id: string | number): string {
  return `${getShareOrigin()}${pathFor(type, id)}`;
}

function pathFor(type: ShareableType, id: string | number, subId?: string | number): string {
  switch (type) {
    case 'live':         return `/live/${id}`;
    case 'room':         return `/room/${id}`;
    case 'practice':     return `/practices/${id}`;
    // CORE-630 — an offline event has no page of its own: it opens as a modal
    // on the calendar, so the shareable form is the calendar with `?eventId=`.
    // `/events/:id` was never in the linking table and resolved to nothing.
    case 'event':        return `/lives?eventId=${id}`;
    case 'master':       return `/u/${id}`;
    case 'user':         return `/u/${id}`;
    case 'master_post':  return subId != null ? `/u/${id}/post/${subId}` : `/u/${id}`;
    case 'gamification': return `/s/${id}`;
  }
}

function serializeQuery(query?: Record<string, string | number | undefined>): string {
  if (!query) return '';
  const parts: string[] = [];
  for (const [k, v] of Object.entries(query)) {
    if (v == null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.join('&');
}
